import React, { useState, useEffect, useMemo } from "react";
import { Box } from "@mui/material";
import { setPageTitle } from "../../utils/document";
import useAuth from "../../hooks/useAuth";
import DashboardLayout from "../../components/layout/DashboardLayout";
import {
  INVESTMENT_LEAGUE_TABS,
  INVESTMENT_LEAGUE_TABS_ROLE_BASE,
  isInvestmentLeagueAdmin,
} from "../../constants/investmentLeague";
import {
  getInvestmentLeagueUser,
  getMacAndGroups,
  getInvestments,
  getInvestmentsByMac,
  getJackpotSummary,
  getJackpotSummaryByMac,
  getJackpotLeaderboard,
  getLeagueLeaderboard,
  getInvestmentRecipientLeaderboard,
} from "../../api/investmentLeague";
import {
  mapInvestmentsResponse,
  mapJackpotSummaryResponse,
  mapJackpotLeaderboardResponse,
  mapLeagueLeaderboardResponse,
  isValidLeagueUserAccess,
} from "./utils/mapApiData";
import InvestmentSummary from "./components/InvestmentSummary";
import JackpotStatus from "./components/JackpotStatus";
import Leaderboard from "./components/Leaderboard";
import RecipientLeaderboard from "./components/RecipientLeaderboard";
import ReinvestQ3 from "./components/ReinvestQ3";
import TabLoader from "./components/TabLoader";

const InvestmentLeague = () => {
  const { user, logout } = useAuth();
  const isAdmin = isInvestmentLeagueAdmin(user?.email);
  const [activeTab, setActiveTab] = useState(isAdmin ? "leaderboard" : "investment-summary");

  const [userValidated, setUserValidated] = useState(false);
  const [validatingUser, setValidatingUser] = useState(true);

  const [macOptions, setMacOptions] = useState([]);
  const [selectedMac, setSelectedMac] = useState(null);

  const [investmentsData, setInvestmentsData] = useState(null);
  const [investmentsLoading, setInvestmentsLoading] = useState(true);
  const [investmentsError, setInvestmentsError] = useState(null);
  const [selectedQuarter, setSelectedQuarter] = useState(null);

  const [jackpotSummary, setJackpotSummary] = useState(null);
  const [jackpotLeaderboard, setJackpotLeaderboard] = useState([]);
  const [jackpotSummaryLoading, setJackpotSummaryLoading] = useState(false);
  const [jackpotLeaderboardLoading, setJackpotLeaderboardLoading] = useState(false);
  const [jackpotError, setJackpotError] = useState(null);
  const [jackpotSummaryFetched, setJackpotSummaryFetched] = useState(false);
  const [jackpotSummaryRetryKey, setJackpotSummaryRetryKey] = useState(0);
  const [jackpotLeaderboardFetched, setJackpotLeaderboardFetched] = useState(false);
  const [jackpotLeaderboardRetryKey, setJackpotLeaderboardRetryKey] = useState(0);

  const [leaderboardData, setLeaderboardData] = useState(null);
  const [leaderboardLoading, setLeaderboardLoading] = useState(false);
  const [leaderboardError, setLeaderboardError] = useState(null);
  const [leaderboardFetched, setLeaderboardFetched] = useState(false);
  const [leaderboardRetryKey, setLeaderboardRetryKey] = useState(0);

  const [recipientData, setRecipientData] = useState(null);
  const [recipientLoading, setRecipientLoading] = useState(false);
  const [recipientError, setRecipientError] = useState(null);
  const [recipientFetched, setRecipientFetched] = useState(false);
  const [recipientRetryKey, setRecipientRetryKey] = useState(0);

  const tabs = useMemo(
    () => (isAdmin ? INVESTMENT_LEAGUE_TABS_ROLE_BASE : INVESTMENT_LEAGUE_TABS),
    [isAdmin]
  );

  useEffect(() => {
    setPageTitle("Investment League");
    if (isAdmin) {
      setActiveTab("leaderboard");
    }
  }, [isAdmin]);

  useEffect(() => {
    if (!user?.email) return;

    let cancelled = false;

    const validateUser = async () => {
      setValidatingUser(true);

      try {
        const userResponse = await getInvestmentLeagueUser();

        if (!cancelled) {
          const hasAccess = isValidLeagueUserAccess(userResponse);
          if (!hasAccess && !isAdmin) {
            logout();
            return;
          }

          setUserValidated(true);
        }
      } catch {
        if (!cancelled) {
          logout();
        }
      } finally {
        if (!cancelled) {
          setValidatingUser(false);
        }
      }
    };

    validateUser();
    return () => { cancelled = true; };
  }, [user?.email, isAdmin, logout]);

  useEffect(() => {
    if (!userValidated || !isAdmin) return;

    let cancelled = false;

    const loadMacOptions = async () => {
      try {
        const macs = await getMacAndGroups();
        if (!cancelled) {
          const options = Array.isArray(macs) ? macs : [];
          setMacOptions(options);
          setSelectedMac(options.length > 0 ? options[0].name : null);
        }
      } catch (err) {
        if (!cancelled) {
          setInvestmentsError(err?.response?.data?.message || "Failed to load MAC list.");
          setInvestmentsLoading(false);
        }
      }
    };

    loadMacOptions();
    return () => { cancelled = true; };
  }, [userValidated, isAdmin]);

  useEffect(() => {
    if (!userValidated) return;
    if (isAdmin && !selectedMac) return;

    let cancelled = false;

    const loadInvestments = async () => {
      setInvestmentsLoading(true);
      setInvestmentsError(null);

      try {
        const response = isAdmin
          ? await getInvestmentsByMac(selectedMac)
          : await getInvestments();

        if (!cancelled) {
          setInvestmentsData(mapInvestmentsResponse(response));
        }
      } catch (err) {
        if (!cancelled) {
          setInvestmentsError(err?.response?.data?.message || "Failed to load investment summary.");
        }
      } finally {
        if (!cancelled) {
          setInvestmentsLoading(false);
        }
      }
    };

    loadInvestments();
    return () => { cancelled = true; };
  }, [userValidated, isAdmin, selectedMac]);

  useEffect(() => {
    if (!userValidated || isAdmin) return;
    if (activeTab !== "jackpot-status" || jackpotSummaryFetched) return;

    let cancelled = false;

    const loadSummary = async () => {
      setJackpotSummaryLoading(true);
      setJackpotError(null);

      try {
        const summaryResponse = await getJackpotSummary();
        if (!cancelled) {
          setJackpotSummary(mapJackpotSummaryResponse(summaryResponse));
          setJackpotSummaryFetched(true);
        }
      } catch (err) {
        if (!cancelled) {
          setJackpotError(err?.response?.data?.message || "Failed to load jackpot status.");
        }
      } finally {
        if (!cancelled) {
          setJackpotSummaryLoading(false);
        }
      }
    };

    loadSummary();
    return () => { cancelled = true; };
  }, [userValidated, isAdmin, activeTab, jackpotSummaryFetched]);

  useEffect(() => {
    if (!userValidated || !isAdmin || !selectedMac) return;

    let cancelled = false;

    const loadSummary = async () => {
      setJackpotSummaryLoading(true);
      setJackpotError(null);

      try {
        const summaryResponse = await getJackpotSummaryByMac(selectedMac);
        if (!cancelled) {
          setJackpotSummary(mapJackpotSummaryResponse(summaryResponse));
        }
      } catch (err) {
        if (!cancelled) {
          setJackpotError(err?.response?.data?.message || "Failed to load jackpot status.");
        }
      } finally {
        if (!cancelled) {
          setJackpotSummaryLoading(false);
        }
      }
    };

    loadSummary();
    return () => { cancelled = true; };
  }, [userValidated, isAdmin, selectedMac, jackpotSummaryRetryKey]);

  useEffect(() => {
    if (!userValidated || jackpotLeaderboardFetched) return;

    let cancelled = false;

    const loadLeaderboard = async () => {
      setJackpotLeaderboardLoading(true);
      setJackpotError(null);

      try {
        const leaderboardResponse = await getJackpotLeaderboard();
        if (!cancelled) {
          setJackpotLeaderboard(
            mapJackpotLeaderboardResponse(leaderboardResponse, user?.macCode)
          );
          setJackpotLeaderboardFetched(true);
        }
      } catch (err) {
        if (!cancelled) {
          setJackpotError(err?.response?.data?.message || "Failed to load jackpot leaderboard.");
        }
      } finally {
        if (!cancelled) {
          setJackpotLeaderboardLoading(false);
        }
      }
    };

    loadLeaderboard();
    return () => { cancelled = true; };
  }, [userValidated, jackpotLeaderboardFetched, jackpotLeaderboardRetryKey, user?.macCode]);

  useEffect(() => {
    if (!userValidated || leaderboardFetched) return;

    let cancelled = false;

    const loadLeaderboard = async () => {
      setLeaderboardLoading(true);
      setLeaderboardError(null);

      try {
        const response = await getLeagueLeaderboard();
        if (!cancelled) {
          setLeaderboardData(mapLeagueLeaderboardResponse(response));
          setLeaderboardFetched(true);
        }
      } catch (err) {
        if (!cancelled) {
          setLeaderboardError(err?.response?.data?.message || "Failed to load league leaderboard.");
        }
      } finally {
        if (!cancelled) {
          setLeaderboardLoading(false);
        }
      }
    };

    loadLeaderboard();
    return () => { cancelled = true; };
  }, [userValidated, leaderboardFetched, leaderboardRetryKey]);

  useEffect(() => {
    if (!userValidated || recipientFetched) return;
    if (activeTab !== "recipient-leaderboard") return;

    let cancelled = false;

    const loadRecipientLeaderboard = async () => {
      setRecipientLoading(true);
      setRecipientError(null);

      try {
        const response = await getInvestmentRecipientLeaderboard();
        if (!cancelled) {
          setRecipientData(response);
          setRecipientFetched(true);
        }
      } catch (err) {
        if (!cancelled) {
          setRecipientError(err?.response?.data?.message || "Failed to load recipient leaderboard.");
        }
      } finally {
        if (!cancelled) {
          setRecipientLoading(false);
        }
      }
    };

    loadRecipientLeaderboard();
    return () => { cancelled = true; };
  }, [userValidated, activeTab, recipientFetched, recipientRetryKey]);

  const retryRecipientData = () => {
    setRecipientFetched(false);
    setRecipientRetryKey((key) => key + 1);
  };

  const retryJackpotSummary = () => {
    if (isAdmin) {
      setJackpotSummaryRetryKey((key) => key + 1);
      return;
    }
    setJackpotSummaryFetched(false);
  };

  const retryJackpotLeaderboard = () => {
    setJackpotLeaderboardFetched(false);
    setJackpotLeaderboardRetryKey((key) => key + 1);
  };

  const retryJackpotData = () => {
    retryJackpotSummary();
    retryJackpotLeaderboard();
  };

  const retryLeaderboardData = () => {
    setLeaderboardFetched(false);
    setLeaderboardRetryKey((key) => key + 1);
  };

  if (validatingUser) {
    return (
      <DashboardLayout
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        subtitle="Investment League"
        macBadge={user?.macCode ?? null}
      >
        <Box sx={{ flex: 1, overflow: "auto", minHeight: 0, px: { xs: 2, sm: 3, md: 4 }, py: { xs: 2, sm: 3 } }}>
          <TabLoader message="Verifying investment league access..." />
        </Box>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout
      tabs={tabs}
      activeTab={activeTab}
      onTabChange={setActiveTab}
      subtitle="Investment League"
      macBadge={user?.macCode ?? null}
    >
      <Box
        sx={{
          flex:          1,
          overflow:      "auto",
          minHeight:     0,
          px:            { xs: 2, sm: 3, md: 4 },
          py:            { xs: 2, sm: 3 },
        }}
      >
        {activeTab === "leaderboard" && (
          <Leaderboard
            data={leaderboardData}
            loading={leaderboardLoading}
            error={leaderboardError}
            onRetry={retryLeaderboardData}
            onUnitClick={() => setActiveTab("investment-summary")}
          />
        )}
        {activeTab === "investment-summary" && (
          <InvestmentSummary
            data={investmentsData}
            loading={investmentsLoading}
            error={investmentsError}
            showMacSelector={isAdmin}
            macOptions={macOptions}
            selectedMac={selectedMac}
            onMacChange={setSelectedMac}
            selectedQuarter={selectedQuarter}
            onQuarterChange={setSelectedQuarter}
          />
        )}
        {activeTab === "jackpot-status" && (
          <JackpotStatus
            summary={jackpotSummary}
            leaderboard={jackpotLeaderboard}
            summaryLoading={jackpotSummaryLoading}
            leaderboardLoading={jackpotLeaderboardLoading}
            error={jackpotError}
            onRetry={retryJackpotData}
            showMacSelector={isAdmin}
            macOptions={macOptions}
            selectedMac={selectedMac}
            onMacChange={setSelectedMac}
          />
        )}
        {activeTab === "recipient-leaderboard" && (
          <RecipientLeaderboard
            data={recipientData}
            loading={recipientLoading}
            error={recipientError}
            onRetry={retryRecipientData}
            isAdmin={isAdmin}
          />
        )}
        {activeTab === "reinvest-q3" && (
          <ReinvestQ3
            isAdmin={isAdmin}
            currentUser={user}
          />
        )}
      </Box>
    </DashboardLayout>
  );
};

export default InvestmentLeague;
